import { serializeDateField, serializeDateFields } from 'common/utils/time';
import { PaginatedApiPayloadV2 } from 'domain/Common';
import { EventListItemApiResponse } from 'domain/event';
import { mapEventTimeFromApi } from 'event/mappers';
import { orderBy } from 'lodash-es';
import flow from 'lodash/fp/flow';
import { transformPaginatedApiPayload } from 'modules/api/utils';

import { InsightsReportData } from '../insightsReport';
import { AnswerStatistics, isTextAnswerStatistics } from '../insightsReport/types';
import {
  CreatedEventsReport,
  CreatedEventsReportApi,
  EventReportData,
  EventReportSource,
  ExecutiveSummaryData,
  ExistingEventsReport,
  ExistingEventsReportApi,
  ReportSettings,
  ReportSettingsApi,
} from './types';

export const mapAnswerStatisticsList = (data: AnswerStatistics[]) =>
  data.map((answerStatistics) =>
    isTextAnswerStatistics(answerStatistics)
      ? {
          ...answerStatistics,
          themes: orderBy(answerStatistics.themes, 'count', 'desc'),
        }
      : answerStatistics
  );

export const mapEventsReportDataToInsightsReportData = ({
  associatedEvents,
  createdAt,
  id,
  name,
  status,
  pin,
  anonymityFilterEnabled,
  anonymityThreshold,
}: EventReportData): InsightsReportData => ({
  associatedItems: associatedEvents.map(serializeDateFields('date')),
  createdAt: serializeDateField(createdAt),
  id: String(id),
  name,
  status,
  pin,
  anonymityFilterEnabled,
  anonymityThreshold,
});

export const mapEventsReportListToInsightsReportList = (
  response: PaginatedApiPayloadV2<EventReportData>
): PaginatedApiPayloadV2<InsightsReportData> => ({
  ...response,
  data: response.data.map(mapEventsReportDataToInsightsReportData),
});

const mapToEventReportSource = (event: {
  id: number;
  publicId?: string;
  name: string;
  startDate: string;
  eventDuration: number;
  eventGuestCount: number;
}): EventReportSource => ({
  eventTime: mapEventTimeFromApi({ id: event.id, startTime: event.startDate, durationInMinutes: event.eventDuration }),
  guestCount: event.eventGuestCount,
  id: event.id,
  publicId: event.publicId,
  name: event.name,
});

export const mapEventsListToEventReportSources = flow(
  transformPaginatedApiPayload<EventListItemApiResponse>,
  (payload): PaginatedApiPayloadV2<EventReportSource> => ({
    totalCount: payload.totalCount,
    data: payload.data.map(flow(serializeDateFields('startDate'), mapToEventReportSource)),
  })
);

export const mapExecutiveSummaryData = (data: ExecutiveSummaryData): ExecutiveSummaryData => {
  // Normalize null to empty string and return raw markdown
  const content = data.content ?? '';

  return {
    ...data,
    content,
  };
};

export const mapCreatedEventsReport = ({ id, regenerated }: CreatedEventsReportApi): CreatedEventsReport => ({
  id,
  regenerated: regenerated ?? false,
});

export const mapExistingEventsReport = ({ reportId, status }: ExistingEventsReportApi): ExistingEventsReport => ({
  reportId: reportId ?? null,
  status: status ?? null,
});

const fromEpochSeconds = (seconds: number | null | undefined): string | undefined =>
  seconds === null || seconds === undefined ? undefined : new Date(seconds * 1000).toISOString();

export const mapReportSettings = ({
  quotesEnabled,
  canManage,
  generatedAt,
  generation,
}: ReportSettingsApi): ReportSettings => ({
  quotesEnabled,
  canManage,
  generatedAt: fromEpochSeconds(generatedAt),
  generation: generation
    ? {
        job: generation.job,
        status: generation.status,
        requestedBy: generation.requestedBy ?? undefined,
        requestedAt: fromEpochSeconds(generation.requestedAt),
        failedAt: fromEpochSeconds(generation.failedAt),
      }
    : undefined,
});
